import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, useHistory } from 'react-router-dom';
import { GRADIENTS, COLORS } from './themeConstants';

const KnowMore = () => {
	const [isVisible, setIsVisible] = useState(false);
	const sectionRef = useRef(null);

	const history = useHistory();

	const handleBackToHome = () => {
		history.push('/');
	};

	useEffect(() => {
		const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 });

		if (sectionRef.current) observer.observe(sectionRef.current);
		return () => observer.disconnect();
	}, []);

	const features = [
		{
			title: 'Academic Management',
			icon: '🎓',
			description: 'Comprehensive academic management including attendance, assessments, and timetabling.',
			details: ['Automated attendance tracking', 'Internal assessment management', 'Exam management system', 'Course planning and scheduling', 'Student performance analytics'],
		},
		{
			title: 'Administrative Tools',
			icon: '⚙️',
			description: 'Streamline administrative tasks with powerful management tools.',
			details: ['Staff management', 'Resource allocation', 'Document management', 'Financial tracking', 'Infrastructure management'],
		},
		{
			title: 'Student Services',
			icon: '👥',
			description: 'Enhanced student experience through digital services.',
			details: ['Student portal access', 'Online fee payment', 'Digital library access', 'Course registration', 'Student support system'],
		},
		{
			title: 'Compliance & Reporting',
			icon: '📱',
			description: 'Ensure compliance with regulatory standards and generate detailed reports.',
			details: ['Regulatory compliance tools', 'Custom report generation', 'NAAC accreditation support', 'AISHE', 'Statistical analysis tools'],
		},
	];

	return (
		<div className={`min-h-screen pt-28 pb-16 px-4 bg-gradient-to-br ${GRADIENTS.background.primary}`}>
			<button
				onClick={handleBackToHome}
				className={`fixed top-20 left-4 z-50 px-4 py-2 rounded-full bg-gradient-to-r 
                    ${GRADIENTS.button.primary} text-white font-medium hover:opacity-90 
                    transition-all duration-300 flex items-center gap-2 md:top-24`}>
				← Back
			</button>

			<div className='container mx-auto px-4 mb-12 md:mb-20'>
				<h1 className={`text-3xl md:text-5xl lg:text-6xl font-bold text-center mb-6 bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent`}>Discover Necttos</h1>
				<p className={`text-base md:text-xl text-center ${COLORS.text.secondary} max-w-3xl mx-auto`}>A comprehensive education management platform designed to revolutionize academic institutions through cutting-edge technology and innovative solutions.</p>
			</div>

			<div className='container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-12 md:mb-20'>
				{features.map((feature, index) => (
					<div
						key={index}
						className={`p-8 rounded-2xl ${COLORS.background.glass} border ${COLORS.border.glass}
                            transform transition-all duration-500 hover:scale-105`}>
						<div className='text-4xl mb-4'>{feature.icon}</div>
						<h3 className={`text-2xl font-bold mb-4 bg-gradient-to-r ${GRADIENTS.text.secondary} bg-clip-text text-transparent`}>{feature.title}</h3>
						<p className={`mb-6 ${COLORS.text.secondary}`}>{feature.description}</p>
						<ul className='space-y-2'>
							{feature.details.map((detail, i) => (
								<li key={i} className={`flex items-center ${COLORS.text.secondary}`}>
									<span className='mr-2'>•</span>
									{detail}
								</li>
							))}
						</ul>
					</div>
				))}
			</div>

			<div className='container mx-auto px-4 text-center mb-12 md:mb-20'>
				<h2 className={`text-2xl md:text-3xl font-bold mb-8 md:mb-12 bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent`}>Why Choose Necttos?</h2>
				<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8'>
					{[
						{ title: 'Time Saving', value: '70%', desc: 'Reduction in administrative tasks' },
						{ title: 'Cost Effective', value: '40%', desc: 'Decrease in operational costs' },
						{ title: 'Efficiency', value: '90%', desc: 'Increase in process efficiency' },
					].map((stat, index) => (
						<div key={index} className={`p-6 rounded-xl ${COLORS.background.glass} border ${COLORS.border.glass}`}>
							<div className={`text-4xl font-bold bg-gradient-to-r ${GRADIENTS.text.primary} bg-clip-text text-transparent`}>{stat.value}</div>
							<h3 className='text-xl font-semibold mt-2'>{stat.title}</h3>
							<p className={`mt-2 ${COLORS.text.secondary}`}>{stat.desc}</p>
						</div>
					))}
				</div>
			</div>

			<div className='container mx-auto px-4 text-center'>
				<div className={`p-6 md:p-12 rounded-2xl ${COLORS.background.glass} border ${COLORS.border.glass}`}>
					<h2 className={`text-3xl font-bold mb-6 bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent`}>Ready to Transform Your Institution?</h2>
					<p className={`mb-8 ${COLORS.text.secondary}`}>Join the growing number of institutions that trust Necttos for their educational management needs.</p>
					<button
						onClick={() => history.push('register')}
						className={`px-8 py-4 rounded-full bg-gradient-to-r ${GRADIENTS.button.primary} 
                        text-white font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300`}>
						Schedule a Demo
					</button>
				</div>
			</div>
		</div>
	);
};

export default KnowMore;
